/**
 * 私链分享落地页（/share/director/:token）
 *
 * 视觉语言参考 EcommerceReplicate：浅蓝磨砂背景 + 浮动光斑 + 深色 hero 渐变
 * 无登录访问。流程：
 * 1. 拉 share info（projectName / authorName / requiresPassword 等）
 * 2. 如果需要密码：显示密码卡片，验证通过后再拉 video URL
 * 3. 否则直接拉 video URL 播放
 */

import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Download, Loader2, Sparkles, Share2, Eye, Clock } from 'lucide-react';
import {
  directorShareApi,
  ShareApiError,
  type ShareLandingInfo,
} from './services/directorShareApi';
import { SharePasswordPrompt } from './components/share/SharePasswordPrompt';
import { PageBlobs } from './components/PageBlobs';
import { resolveBrowserMediaUrl } from '../../utils/storageUrl';

export function DirectorSharePage() {
  const { token } = useParams<{ token: string }>();
  const [info, setInfo] = useState<ShareLandingInfo | null>(null);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordVerified, setPasswordVerified] = useState(false);

  useEffect(() => {
    if (!token) return;
    directorShareApi
      .getShareInfo(token)
      .then((data) => setInfo(data))
      .catch((err) => {
        setError(err instanceof ShareApiError ? err.message : '链接已失效');
      })
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => {
    if (!token || !info || info.requiresPassword || passwordVerified) return;
    if (info.requiresPassword === false) {
      directorShareApi
        .getVideoUrl(token)
        .then((data) => {
          setVideoUrl(data.videoUrl);
          setDownloadUrl(data.downloadUrl);
        })
        .catch((err) => {
          setError(err instanceof ShareApiError ? err.message : '视频不可用');
        });
    }
  }, [token, info, passwordVerified]);

  const handleVerifyPassword = async (password: string) => {
    if (!token) return;
    setPasswordError(null);
    try {
      await directorShareApi.verifyPassword(token, password);
      const data = await directorShareApi.getVideoUrl(token, password);
      setVideoUrl(data.videoUrl);
      setDownloadUrl(data.downloadUrl);
      setPasswordVerified(true);
    } catch (err) {
      setPasswordError(err instanceof ShareApiError ? err.message : '密码错误');
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#eef3f8]">
        <Loader2 className="h-6 w-6 animate-spin text-sky-500" />
      </div>
    );
  }

  if (error || !info) {
    return (
      <div className="relative min-h-screen bg-[#eef3f8]">
        <PageBlobs />
        <div className="relative flex min-h-screen flex-col items-center justify-center p-6 text-center">
          <div className="rounded-[28px] border border-white/70 bg-white/85 p-10 shadow-[0_24px_80px_rgba(15,23,42,0.08)] backdrop-blur-xl">
            <p className="mb-3 text-2xl font-semibold text-slate-900">{error ?? '链接已失效'}</p>
            <p className="mb-6 text-slate-500">请联系作者获取新链接</p>
            <a
              href="/"
              className="inline-flex items-center justify-center rounded-2xl bg-slate-950 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-slate-300/70 transition hover:bg-slate-800"
            >
              访问首页
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen bg-[#eef3f8]">
      <PageBlobs />

      <header className="relative border-b border-white/70 bg-white/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-3">
          <a href="/" className="flex items-center gap-2 text-slate-900">
            <Sparkles className="h-5 w-5 text-sky-500" />
            <span className="font-semibold">智图 · 短剧分享</span>
          </a>
          <button
            type="button"
            onClick={() => navigator.clipboard.writeText(window.location.href)}
            className="inline-flex items-center gap-1.5 rounded-2xl border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:border-sky-200 hover:text-sky-700"
          >
            <Share2 className="h-3.5 w-3.5" /> 复制链接
          </button>
        </div>
      </header>

      <main className="relative mx-auto max-w-3xl px-4 py-10">
        {info.requiresPassword && !passwordVerified ? (
          <SharePasswordPrompt onSubmit={handleVerifyPassword} error={passwordError} />
        ) : (
          <div className="space-y-5">
            {/* 标题区 */}
            <div className="overflow-hidden rounded-[28px] bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800 p-6 text-center text-white shadow-2xl shadow-slate-300/40">
              <div className="absolute right-[-60px] top-[-40px] h-40 w-40 rounded-full bg-sky-400/20 blur-3xl" />
              <div className="relative">
                <div className="mb-3 inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1 text-xs font-medium text-slate-200">
                  <Sparkles className="h-3.5 w-3.5 text-sky-200" />
                  AI 短剧
                </div>
                <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">
                  《{info.projectName}》
                </h1>
                {info.authorName && (
                  <p className="mt-2 text-sm text-slate-300">作者：{info.authorName}</p>
                )}
              </div>
            </div>

            {/* 视频播放器 */}
            <div className="overflow-hidden rounded-[28px] bg-black shadow-2xl shadow-slate-950/30">
              {videoUrl ? (
                <video src={resolveBrowserMediaUrl(videoUrl)} controls className="w-full" />
              ) : (
                <div className="flex aspect-video items-center justify-center text-white/60">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              )}
            </div>

            {/* 元信息 + 操作 */}
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-white/70 bg-white/85 px-4 py-3 shadow-[0_12px_40px_rgba(15,23,42,0.06)] backdrop-blur-xl">
              <div className="flex flex-wrap items-center gap-3 text-sm text-slate-500">
                <span className="inline-flex items-center gap-1.5">
                  <Eye className="h-4 w-4 text-sky-500" />
                  已观看 {info.viewCount} 次
                </span>
                {info.compositionDuration && (
                  <span className="inline-flex items-center gap-1.5">
                    <Clock className="h-4 w-4 text-cyan-500" />
                    时长 {Math.round(info.compositionDuration)}s
                  </span>
                )}
              </div>
              {info.allowDownload && downloadUrl && (
                <a
                  href={downloadUrl}
                  download="director-video.mp4"
                  className="inline-flex items-center gap-1.5 rounded-2xl bg-slate-950 px-4 py-2 text-sm font-semibold text-white shadow-md transition hover:bg-slate-800"
                >
                  <Download className="h-4 w-4" /> 下载 mp4
                </a>
              )}
            </div>

            {info.projectDescription && (
              <div className="rounded-2xl border border-white/70 bg-white/85 p-4 text-sm leading-6 text-slate-700 shadow-[0_12px_40px_rgba(15,23,42,0.06)] backdrop-blur-xl">
                {info.projectDescription}
              </div>
            )}

            {/* 社交分享 */}
            <div className="flex flex-wrap items-center justify-center gap-3 rounded-2xl border border-white/70 bg-white/85 p-4 shadow-[0_12px_40px_rgba(15,23,42,0.06)] backdrop-blur-xl">
              <span className="text-xs text-slate-500">分享到：</span>
              <button
                type="button"
                onClick={() => navigator.clipboard.writeText(window.location.href)}
                className="rounded-2xl border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:border-sky-200 hover:text-sky-700"
                title="复制链接，可粘贴到微信"
              >
                微信（复制链接）
              </button>
              <a
                href={`https://service.weibo.com/share/share.php?url=${encodeURIComponent(window.location.href)}&title=${encodeURIComponent(`《${info.projectName}》- AI 短剧创作`)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-2xl border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:border-sky-200 hover:text-sky-700"
              >
                微博
              </a>
              <button
                type="button"
                onClick={() => navigator.clipboard.writeText(window.location.href)}
                className="rounded-2xl border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:border-sky-200 hover:text-sky-700"
              >
                复制链接
              </button>
            </div>

            {/* 引流 */}
            <div className="overflow-hidden rounded-[28px] border border-white/70 bg-gradient-to-br from-sky-100 via-cyan-50 to-amber-50 p-7 text-center shadow-[0_18px_60px_rgba(15,23,42,0.06)] backdrop-blur-xl">
              <p className="text-base font-semibold text-slate-900">想做一部你自己的短剧？</p>
              <p className="mt-1.5 text-sm text-slate-600">用 AI 把脑海里的画面变成视频</p>
              <a
                href="/"
                className="mt-5 inline-flex items-center justify-center rounded-2xl bg-slate-950 px-6 py-2.5 text-sm font-semibold text-white shadow-lg shadow-slate-300/70 transition hover:scale-[1.02] hover:bg-slate-800"
              >
                立即体验
              </a>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
